import React, { useContext, useEffect, useMemo } from "react";
import OrderInfo from "./fields/info/info_field";
import OrderCustomers from "./fields/customers/customers_field";
import OrderTransport from "./fields/transport/transport_field";
import OrderAccomodation from "./fields/accomodation/accomodation_field";
import OrderHotels from "./fields/accomodation/hotels_field";
import OrderServices from "./fields/order_services/services_field";
import OrderAdvances from "./fields/advances/advances_field";
import OrderPrice from "./fields/price/price_field";
import {
  getProduct,
  getTerm,
  getRoutes,
  calculateOrderPrice,
  createOrder,
} from "../queries";
import {
  useIsFetching,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { OrderContext } from "../contexts/order_context";
import { useForm, FormProvider } from "react-hook-form";
import OrderFieldSpinner from "./fields/shared/order_field_spinner";
import { isPresent } from "../../../helpers/application_helper";
import { isDDMMYYYY } from "../helpers";

const OrderForm = () => {
  const { productId, termId, townId } = useContext(OrderContext);
  const queryClient = useQueryClient();
  const isAnyQueryFetching = useIsFetching();
  const methods = useForm({
    mode: "onChange",
  });
  const {
    register,
    handleSubmit,
    setError,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = methods;

  const [
    adults,
    children,
    children_date_births,
    rooms,
    room_configs,
    extra_room_configs,
    services,
    term2,
    meals,
    extra_meals,
    route_start,
    route_end,
    show_hotels,
    email,
    room_configs_people,
    extra_room_configs_people,
  ] = watch([
    "adults",
    "children",
    "children_date_births",
    "rooms",
    "room_configs",
    "extra_room_configs",
    "services",
    "term2",
    "meals",
    "extra_meals",
    "route_start",
    "route_end",
    "show_hotels",
    "email",
    "room_configs_people",
    "extra_room_configs_people",
  ]);
  const inputs = {
    adults,
    children,
    children_date_births,
    rooms,
    room_configs,
    extra_room_configs,
    services,
    term2,
    meals,
    extra_meals,
    route_start,
    route_end,
    show_hotels,
    email,
  };

  // Queries

  const { data: productData, isFetched: productIsFetched } = useQuery(
    ["product"],
    () => getProduct({ productId }),
    {
      onError: (error) => alert(error),
    }
  );

  const {
    data: termData,
    isFetching: termIsFetching,
    isFetched: termIsFetched,
  } = useQuery(["term"], () => getTerm({ termId }), {
    onError: (error) => alert(error),
  });

  const routesQuery = useQuery(
    ["routes"],
    () =>
      getRoutes({
        routeStartId: getValues("route_start_id"),
        routeEndId: getValues("route_end_id"),
        term1Id: termId,
        term2Id: getValues("term2"),
        people:
          parseInt(getValues("adults").value) +
          parseInt(getValues("children").value),
        townId: townId,
      }),
    {
      enabled:
        !!(typeof getValues === "undefined") &&
        isPresent(getValues("adults")) &&
        isPresent(getValues("children")),
      // onError: (error) => alert(error),
    }
  );

  const orderDetails = {
    adults: getValues("adults.value"),
    children: getValues("children.value"),
    children_date_births: getValues("children_date_births")?.filter((n) => n),
    rooms: getValues("rooms.value"),
    meal1_id: getValues("meals.one.id"),
    meal2_id: getValues("meals.two.id"),
    meal1_extra_id: getValues("extra_meals.one.id"),
    meal2_extra_id: getValues("extra_meals.two.id"),
    room_config1_id: getValues("room_configs.one.id"),
    room_config2_id: getValues("room_configs.two.id"),
    room_config1_service_id: getValues("room_configs.one.service"),
    room_config2_service_id: getValues("room_configs.two.service"),
    room_config1_extra_id: getValues("extra_room_configs.one.id"),
    room_config2_extra_id: getValues("extra_room_configs.two.id"),
    room_config1_extra_service_id: getValues("extra_room_configs.one.service"),
    room_config2_extra_service_id: getValues("extra_room_configs.two.service"),
    term1_id: termId,
    term2_id: getValues("term2"),
    route_start_id: getValues("route_start.value"),
    route_end_id: getValues("term2")
      ? getValues("route_end2.value") || getValues("route_end.value") // TODO: Prevent calculating price when routesQuery is fetching end route for term2
      : getValues("route_end.value"),
    order_services: getValues("services")?.filter((n) => n),
    show_hotels: getValues("show_hotels"),
  };

  // Room configs are refetched after people count changes, until then the selection is for the previous count
  const people =
    parseInt(orderDetails.adults) + parseInt(orderDetails.children);
  const roomConfigsMatchPeople =
    room_configs_people === people &&
    (!orderDetails.show_hotels ||
      !isPresent(orderDetails.room_config1_extra_id) ||
      extra_room_configs_people === people);

  const orderQuery = useQuery(
    ["price", orderDetails],
    () =>
      calculateOrderPrice({
        orderDetails: orderDetails,
      }),
    {
      enabled:
        isPresent(orderDetails.adults) &&
        isPresent(orderDetails.children) &&
        isPresent(orderDetails.rooms) &&
        isPresent(orderDetails.term1_id) &&
        isPresent(orderDetails.route_start_id) &&
        isPresent(orderDetails.route_end_id) &&
        isPresent(orderDetails.meal1_id) &&
        isPresent(orderDetails.room_config1_id) &&
        roomConfigsMatchPeople &&
        parseInt(orderDetails.children) === (orderDetails.children_date_births?.length || 0) &&
        routesQuery.isFetched,
      onError: (error) => console.log(error),
    }
  );

  const createOrderMutation = useMutation(createOrder, {
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      // TODO: Fancier alert
      alert(error.response.data.errors);
    },
  });

  // Handlers

  const handleCreateOrder = () => {
    createOrderMutation.mutate({
      orderDetails: {
        adults: getValues("adults.value"),
        children: getValues("children.value"),
        children_date_births: getValues("children_date_births"),
        rooms: getValues("rooms.value"),
        meal1_id: getValues("meals.one.id"),
        meal2_id: getValues("meals.two.id"),
        meal1_extra_id: getValues("extra_meals.one.id"),
        meal2_extra_id: getValues("extra_meals.two.id"),
        room_config1_id: getValues("room_configs.one.id"),
        room_config2_id: getValues("room_configs.two.id"),
        room_config1_service: getValues("room_configs.one.service"),
        room_config2_service: getValues("room_configs.two.service"),
        room_config1_extra_id: getValues("extra_room_configs.one.id"),
        room_config2_extra_id: getValues("extra_room_configs.two.id"),
        room_config1_extra_service: getValues("extra_room_configs.one.service"),
        room_config2_extra_service: getValues("extra_room_configs.two.service"),
        term1_id: termId,
        term2_id: getValues("term2"),
        route_start_id: getValues("route_start.value"),
        route_end_id: getValues("term2")
          ? getValues("route_end2.value")
          : getValues("route_end.value"),
        order_services: getValues("services"),
        show_hotels: getValues("show_hotels"),
        customer_email: getValues("email"),
        travella_shown_price_log_id: orderQuery.data?.travella_shown_id,
        total_price_shown_czk: orderQuery.data?.price,
      },
    });
  };

  const handleCalculatePrice = () => {
    if (orderDetails.children != orderDetails.children_date_births?.length) {
      return;
    }
    if (
      orderDetails.children_date_births?.some((element) => !isDDMMYYYY(element))
    ) {
      return;
    }
    if (routesQuery.isFetching || termIsFetching || isAnyQueryFetching) {
      return;
    } else if (
      !isPresent(getValues("adults")) ||
      !isPresent(getValues("children")) ||
      !isPresent(getValues("rooms")) ||
      !isPresent(getValues("route_start")) ||
      !isPresent(getValues("meals")) ||
      !isPresent(getValues("services"))
    ) {
      setTimeout(handleCalculatePrice, 750);
    } else {
      orderQuery.refetch();
    }
  };

  useMemo(() => {
    if (inputs.show_hotels && !inputs.term2) return;

    handleCalculatePrice();
  }, [
    inputs.adults,
    // inputs.children,
    inputs.children_date_births,
    inputs.rooms,
    inputs.route_start,
    inputs.route_end,
    inputs.route_end2,
    inputs.meals?.one?.id,
    inputs.meals?.two?.id,
    inputs.extra_meals?.one?.id,
    inputs.extra_meals?.two?.id,
    inputs.room_configs?.one?.id,
    inputs.room_configs?.two?.id,
    inputs.room_configs?.one?.service,
    inputs.room_configs?.two?.service,
    inputs.extra_room_configs?.one?.id,
    inputs.extra_room_configs?.two?.id,
    inputs.extra_room_configs?.one?.service,
    inputs.extra_room_configs?.two?.service,
    inputs.services,
  ]);

  useEffect(() => {
    if (!inputs.term2) return;

    setValue("term2", null);
    setValue("extra_room_configs.one.id", null);
    setValue("extra_meals.one.id", null);
  }, [inputs.show_hotels]);

  useEffect(() => {
    routesQuery.refetch();
  }, [inputs.term2, inputs.adults, inputs.children]);

  return (
    <section className="order-form">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleCreateOrder)}>
          <OrderInfo product={productData} term={termData} />
          <OrderCustomers />
          {routesQuery.isLoading || routesQuery.isFetching ? (
            <OrderFieldSpinner />
          ) : (
            <OrderTransport
              routes={routesQuery.data}
              isError={routesQuery.isError}
            />
          )}
          <OrderAccomodation />
          {["program_hotel", "program"].includes(
            productData?.tour_type.ident
          ) &&
            termData && <OrderHotels term={termData} />}
          <OrderServices />
          {productData?.season_id && termData?.date_start && (
            <OrderAdvances
              seasonId={productData.season_id}
              dateStart={termData.date_start}
              advancePrice={orderQuery.data?.price_advance}
            />
          )}
          <OrderPrice
            price={orderQuery.data?.price}
            priceTravella={orderQuery.data?.price_travella}
            priceSystem={orderQuery.data?.price_system}
            isLoading={
              orderQuery.isLoading ||
              orderQuery.isFetching ||
              routesQuery.isFetching
            }
          />
        </form>
      </FormProvider>
    </section>
  );
};

export default OrderForm;
